import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
  Alert,
  TextInput,
  Platform,
  StatusBar
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AdminLayout from '../components/layout/AdminLayout';
import { Ionicons } from '@expo/vector-icons';
import { getApiUrl } from '../../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── API helper ─────────────────────────────────────────────────────────────
const apiCall = async (endpoint, options = {}) => {
  const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
  const response = await fetch(`${getApiUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${response.status})`);
  }
  return response.json();
};

// ─── Reward type meta ────────────────────────────────────────────────────────
const REWARD_META = {
  mbs:    { icon: '📦', label: 'MBs',          color: '#3B82F6', bg: '#EFF6FF' },
  cash:   { icon: '💵', label: 'Cash',         color: '#059669', bg: '#ECFDF5' },
  points: { icon: '⭐', label: 'Points',       color: '#D97706', bg: '#FFFBEB' },
  fun:    { icon: '🎮', label: 'Fun Challenge', color: '#7C3AED', bg: '#F5F3FF' }
};

// ─── Avatar initials ─────────────────────────────────────────────────────────
const Avatar = ({ name, size = 40 }) => {
  const initials = (name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const colors = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];
  const idx = (name || '').charCodeAt(0) % colors.length;
  return (
    <View style={[styles.avatar, { width: size, height: size, borderRadius: size / 2, backgroundColor: colors[idx] }]}>
      <Text style={[styles.avatarText, { fontSize: size * 0.38 }]}>{initials}</Text>
    </View>
  );
};

// ─── Score Ring ───────────────────────────────────────────────────────────────
const ScoreRing = ({ score, passed }) => {
  const color = passed ? '#059669' : '#DC2626';
  return (
    <View style={[styles.scoreRing, { borderColor: color }]}>
      <Text style={[styles.scoreText, { color }]}>{score}%</Text>
      <Text style={[styles.scoreLabel, { color }]}>{passed ? 'PASS' : 'FAIL'}</Text>
    </View>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const HoneyPotAttemptsScreen = ({ route, navigation }) => {
  const { challenge: routeChallenge } = route.params || {};

  const [challenge, setChallenge] = useState(routeChallenge || null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewarding, setRewarding] = useState(null); // attempt id being rewarded
  const [search, setSearch] = useState('');

  // ─── Fetch attempts ──────────────────────────────────────────────────────
  const fetchAttempts = useCallback(async () => {
    if (!challenge?.id) return;
    try {
      setLoading(true);
      const data = await apiCall(`/honeypot/admin/challenges/${challenge.id}/attempts`);
      // Backend now returns { challenge, attempts }
      if (data.challenge) setChallenge(data.challenge);
      if (Array.isArray(data.attempts)) setAttempts(data.attempts);
      else if (Array.isArray(data)) setAttempts(data); // fallback if old format
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to load attempts: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [challenge?.id]);

  useEffect(() => { fetchAttempts(); }, [fetchAttempts]);

  // ─── Manual reward ────────────────────────────────────────────────────────
  const handleReward = async (attemptId) => {
    try {
      setRewarding(attemptId);
      await apiCall(`/honeypot/admin/attempts/${attemptId}/reward`, { method: 'POST' });
      Alert.alert('✅ Reward Sent', 'The user has been rewarded successfully.');
      fetchAttempts();
    } catch (err) {
      Alert.alert('Error', err.message || 'Failed to award reward');
    } finally {
      setRewarding(null);
    }
  };

  // ─── Bulk reward ──────────────────────────────────────────────────────────
  const handleBulkReward = () => {
    const pending = attempts.filter(a => a.passed && !a.bundleAwarded);
    if (pending.length === 0) {
      return Alert.alert('Info', 'All passing participants have already been rewarded.');
    }

    const rewardMeta = REWARD_META[challenge?.rewardType] || REWARD_META.mbs;
    const confirmMsg = challenge?.rewardType === 'fun'
      ? `Unlock the fun bonus challenge for ${pending.length} passer(s)?`
      : `Award ${rewardMeta.label} to ${pending.length} passer(s)?`;

    if (Platform.OS === 'web') {
      if (window.confirm(confirmMsg)) { doBulkReward(); }
    } else {
      Alert.alert('Bulk Reward', confirmMsg, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Proceed', onPress: doBulkReward }
      ]);
    }
  };

  const doBulkReward = async () => {
    try {
      setLoading(true);
      const res = await apiCall(`/honeypot/admin/challenges/${challenge.id}/bulk-reward`, { method: 'POST' });
      Alert.alert('✅ Done', res.message || 'Rewards sent!');
      fetchAttempts();
    } catch (err) {
      Alert.alert('Error', 'Bulk reward failed: ' + err.message);
      setLoading(false);
    }
  };

  // ─── Filtered attempts ────────────────────────────────────────────────────
  const filtered = attempts.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    const u = a.user || {};
    const participant = u.participant || {};
    const fcp = participant.fcp || {};
    const cluster = fcp.cluster || {};
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.toLowerCase().includes(q) ||
      fcp.name?.toLowerCase().includes(q) ||
      cluster.name?.toLowerCase().includes(q)
    );
  });

  // ─── Stats bar ─────────────────────────────────────────────────────────────
  const passers = attempts.filter(a => a.passed).length;
  const rewarded = attempts.filter(a => a.bundleAwarded).length;
  const pending = attempts.filter(a => a.passed && !a.bundleAwarded).length;
  const passRate = attempts.length > 0 ? Math.round((passers / attempts.length) * 100) : 0;

  const rewardMeta = REWARD_META[challenge?.rewardType] || REWARD_META.mbs;

  // ─── Render attempt card ──────────────────────────────────────────────────
  const renderAttempt = ({ item }) => {
    const u = item.user || {};
    const participant = u.participant || {};
    const fcp = participant.fcp || {};
    const cluster = fcp.cluster || {};

    const timeTaken = item.timeTakenSeconds
      ? `${Math.floor(item.timeTakenSeconds / 60)}m ${item.timeTakenSeconds % 60}s`
      : null;

    const roleColor = u.role === 'admin' ? '#7C3AED' : u.role === 'staff' ? '#2563EB' : '#059669';

    return (
      <View style={styles.card}>
        {/* Left: Avatar + identity */}
        <View style={styles.cardLeft}>
          <Avatar name={u.name} size={44} />
          <View style={styles.identity}>
            <Text style={styles.userName}>{u.name || 'Unknown'}</Text>
            <Text style={styles.userEmail}>{u.email || '—'}</Text>
            <View style={styles.tagRow}>
              {!!u.role && (
                <View style={[styles.roleBadge, { backgroundColor: roleColor }]}>
                  <Text style={styles.roleText}>{(u.role || '').toUpperCase()}</Text>
                </View>
              )}
              {!!(u.userType || u.role) && u.userType && (
                <View style={[styles.roleBadge, { backgroundColor: '#6B7280' }]}>
                  <Text style={styles.roleText}>{u.userType.toUpperCase()}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Middle: Contact + FCP */}
        <View style={styles.cardMid}>
          <View style={styles.metaRow}>
            <Ionicons name="call-outline" size={12} color="#9CA3AF" style={{ marginRight: 4 }} />
            <Text style={styles.metaText}>{u.phone || 'No phone'}</Text>
          </View>
          {!!fcp.name && (
            <View style={styles.metaRow}>
              <Ionicons name="business-outline" size={12} color="#9CA3AF" style={{ marginRight: 4 }} />
              <Text style={styles.metaText} numberOfLines={1}>{fcp.name}</Text>
            </View>
          )}
          {!!cluster.name && (
            <View style={styles.metaRow}>
              <Ionicons name="location-outline" size={12} color="#9CA3AF" style={{ marginRight: 4 }} />
              <Text style={styles.metaText} numberOfLines={1}>{cluster.name}</Text>
            </View>
          )}
          {!!timeTaken && (
            <View style={styles.metaRow}>
              <Ionicons name="time-outline" size={12} color="#9CA3AF" style={{ marginRight: 4 }} />
              <Text style={styles.metaText}>{timeTaken}</Text>
            </View>
          )}
        </View>

        {/* Right: Score + reward action */}
        <View style={styles.cardRight}>
          <ScoreRing score={item.score || 0} passed={item.passed} />
          <View style={{ marginTop: 8, alignItems: 'center' }}>
            {item.bundleAwarded ? (
              <View style={[styles.giftedBadge, { backgroundColor: rewardMeta.bg }]}>
                <Text style={{ fontSize: 12 }}>{rewardMeta.icon}</Text>
                <Text style={[styles.giftedText, { color: rewardMeta.color }]}>GIFTED</Text>
              </View>
            ) : item.passed ? (
              <TouchableOpacity
                style={styles.rewardBtn}
                onPress={() => handleReward(item.id)}
                disabled={rewarding === item.id}
              >
                {rewarding === item.id
                  ? <ActivityIndicator size="small" color="white" />
                  : <Text style={styles.rewardBtnText}>REWARD</Text>
                }
              </TouchableOpacity>
            ) : (
              <Text style={styles.noRewardText}>No Reward</Text>
            )}
          </View>
        </View>
      </View>
    );
  };

  // ─── Header Stats ─────────────────────────────────────────────────────────
  const renderHeader = () => (
    <View>
      {/* Challenge Summary Card */}
      <View style={styles.summaryCard}>
        <View style={styles.summaryRow}>
          <View style={styles.summaryItem}>
            <Text style={styles.summaryVal}>{attempts.length}</Text>
            <Text style={styles.summaryKey}>Participants</Text>
          </View>
          <View style={styles.summaryDivider} />
          <View style={styles.summaryItem}>
            <Text style={[styles.summaryVal, { color: '#059669' }]}>{passRate}%</Text>
            <Text style={styles.summaryKey}>Pass Rate</Text>
          </View>
          <View style={styles.summaryDivider} />
          <View style={styles.summaryItem}>
            <Text style={[styles.summaryVal, { color: '#F59E0B' }]}>{rewarded}</Text>
            <Text style={styles.summaryKey}>Rewarded</Text>
          </View>
          <View style={styles.summaryDivider} />
          <View style={styles.summaryItem}>
            <Text style={[styles.summaryVal, { color: '#EF4444' }]}>{pending}</Text>
            <Text style={styles.summaryKey}>Pending</Text>
          </View>
        </View>

        {/* Reward type badge */}
        <View style={[styles.rewardTypeBadge, { backgroundColor: rewardMeta.bg }]}>
          <Text style={{ fontSize: 14, marginRight: 5 }}>{rewardMeta.icon}</Text>
          <Text style={[styles.rewardTypeLabel, { color: rewardMeta.color }]}>
            Reward: {rewardMeta.label}
            {challenge?.bundleRewardLabel ? ` — ${challenge.bundleRewardLabel}` : ''}
          </Text>
        </View>
      </View>

      {/* Search */}
      <View style={styles.searchWrap}>
        <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by name, email, phone, FCP..."
          placeholderTextColor="#9CA3AF"
          value={search}
          onChangeText={setSearch}
        />
        {!!search && (
          <TouchableOpacity onPress={() => setSearch('')}>
            <Ionicons name="close-circle" size={16} color="#9CA3AF" />
          </TouchableOpacity>
        )}
      </View>

      {filtered.length > 0 && (
        <Text style={styles.resultCount}>{filtered.length} participant{filtered.length !== 1 ? 's' : ''}</Text>
      )}
    </View>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <AdminLayout title="Challenge Attempts" navigation={navigation}>
      <SafeAreaView style={styles.root} edges={['top', 'bottom']}>
      <StatusBar barStyle="dark-content" backgroundColor="#F9FAFB" />

      {/* Top Navigation Bar */}
      <View style={styles.topBar}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={20} color="#1F2937" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginHorizontal: 12 }}>
          <Text style={styles.pageTitle} numberOfLines={1}>
            {challenge?.title || 'View Attempts'}
          </Text>
          <Text style={styles.pageSubtitle}>Attempt Analytics & Rewards</Text>
        </View>
        {pending > 0 && (
          <TouchableOpacity style={styles.bulkBtn} onPress={handleBulkReward}>
            <Ionicons name="flash" size={14} color="white" style={{ marginRight: 5 }} />
            <Text style={styles.bulkBtnText}>Bulk Reward ({pending})</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Content */}
      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#F59E0B" />
          <Text style={styles.loadingText}>Loading attempts...</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={item => item.id.toString()}
          renderItem={renderAttempt}
          ListHeaderComponent={renderHeader}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Text style={{ fontSize: 32 }}>🍯</Text>
              <Text style={styles.emptyText}>
                {search ? 'No participants match your search.' : 'No attempts yet for this challenge.'}
              </Text>
            </View>
          }
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onRefresh={fetchAttempts}
          refreshing={loading}
        />
      )}
    </SafeAreaView>
    </AdminLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F9FAFB'
  },

  // Top Bar
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center'
  },
  pageTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#111827'
  },
  pageSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 1
  },
  bulkBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10B981',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3
  },
  bulkBtnText: {
    color: 'white',
    fontWeight: '700',
    fontSize: 12
  },

  // Summary Card
  summaryCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    marginBottom: 14
  },
  summaryItem: {
    alignItems: 'center',
    flex: 1
  },
  summaryVal: {
    fontSize: 22,
    fontWeight: '800',
    color: '#111827'
  },
  summaryKey: {
    fontSize: 11,
    color: '#6B7280',
    marginTop: 2,
    fontWeight: '500'
  },
  summaryDivider: {
    width: 1,
    height: 36,
    backgroundColor: '#F3F4F6'
  },
  rewardTypeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10
  },
  rewardTypeLabel: {
    fontSize: 13,
    fontWeight: '700'
  },

  // Search
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#1F2937',
    paddingVertical: 0
  },
  resultCount: {
    fontSize: 12,
    color: '#9CA3AF',
    marginLeft: 20,
    marginTop: 8,
    fontWeight: '500'
  },

  // List
  listContent: {
    paddingBottom: 40
  },

  // Attempt Card
  card: {
    flexDirection: 'row',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 10,
    borderRadius: 14,
    padding: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 1
  },
  cardLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 2.2
  },
  avatar: {
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10
  },
  avatarText: {
    color: 'white',
    fontWeight: '700'
  },
  identity: {
    flex: 1
  },
  userName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2
  },
  userEmail: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 4
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4
  },
  roleBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4
  },
  roleText: {
    fontSize: 9,
    color: 'white',
    fontWeight: '700'
  },
  cardMid: {
    flex: 1.8,
    paddingHorizontal: 10,
    borderLeftWidth: 1,
    borderLeftColor: '#F9FAFB',
    gap: 3
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2
  },
  metaText: {
    fontSize: 11,
    color: '#4B5563',
    flex: 1
  },
  cardRight: {
    flex: 1.2,
    alignItems: 'center',
    justifyContent: 'center'
  },

  // Score Ring
  scoreRing: {
    width: 54,
    height: 54,
    borderRadius: 27,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white'
  },
  scoreText: {
    fontSize: 13,
    fontWeight: '800',
    lineHeight: 15
  },
  scoreLabel: {
    fontSize: 8,
    fontWeight: '700',
    letterSpacing: 0.5
  },

  // Reward status
  giftedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 8,
    gap: 3
  },
  giftedText: {
    fontSize: 9,
    fontWeight: '800'
  },
  rewardBtn: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    minWidth: 60,
    alignItems: 'center',
    shadowColor: '#3B82F6',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 2
  },
  rewardBtnText: {
    color: 'white',
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.5
  },
  noRewardText: {
    fontSize: 10,
    color: '#9CA3AF',
    fontWeight: '500'
  },

  // Loading / Empty
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  loadingText: {
    marginTop: 10,
    color: '#6B7280',
    fontSize: 13
  },
  empty: {
    alignItems: 'center',
    paddingTop: 60,
    paddingHorizontal: 32
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    marginTop: 10,
    textAlign: 'center',
    lineHeight: 20
  }
});

export default HoneyPotAttemptsScreen;
